import React, { useCallback, useEffect, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { IconCogAndUser } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";
import { InertiaLink } from "@inertiajs/inertia-react";
import { usePayload } from "@/Contexts/PayloadContext";
import { Inertia } from "@inertiajs/inertia";
import { toast } from "react-toastify";
import { showToast } from "@/Utilities/Utilities";
import AgencyFormPart from "@/Pages/Superuser/Agencies/Partials/AgencyFormPart";
import AgencyDialogCenter from "@/Pages/Superuser/Agencies/Partials/AgencyDialogCenter";

export default function Edit({ organization, owner, associated_files }) {
  const formRef = useRef(null);

  const data = {
    ...organization.data,
    ...{ vat_number: organization.data?.billing?.vat_number },
    ...owner,
    associated_files,
  };

  Object.keys(organization.data.billing).forEach((key) => {
    if (key === "vat_number" || key === "pricing") {
      return;
    }
    data[`billing_${key}`] = organization.data.billing[key];
  });

  Object.keys(organization.data.billing.pricing).forEach((key) => {
    data[`billing_${key}`] = organization.data.billing.pricing[key];
  });

  return (
    <form ref={formRef} className="flex flex-col gap-4">
      <AgencyFormPart organization={data} />
      <Action formRef={formRef} organization={organization.data} />
    </form>
  );
}

Edit.layout = (page) => (
  <AppLayout title="Edit global account">
    <OrganizationLayout
      header="Edit global account"
      icon={
        <LayoutIcon>
          <IconCogAndUser size={50} className="text-black" />
        </LayoutIcon>
      }
    >
      {page}
    </OrganizationLayout>
  </AppLayout>
);

const Action = ({ formRef, organization }) => {
  const { payload, setPayload } = usePayload();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInactivateModal, setShowInactivateModal] = useState(false);

  const submit = useCallback(
    (e) => {
      e.preventDefault();

      if (isDisabled()) {
        return;
      }

      const id = toast.loading("Please wait...");

      const options = {
        onSuccess: () => {
          showToast(id, "Your changes were successfully saved");

          setPayload((prev) => ({
            ...prev,
            new_files: [],
            removed_files: [],
          }));
        },
        onError: () => {
          showToast(
            id,
            "An error occurred. Please check your fields and try again.",
            "error",
          );
        },
      };

      if (payload?.files?.length > 0) {
        options.forceFormData = true;
      }

      Inertia.post(
        route("admin.agencies.update", payload?.id),
        {
          name: payload?.name,
          organization_number: payload?.organization_number,
          email: payload?.email,
          phone: payload?.phone,
          comment: payload?.comment,
          firstname: payload?.firstname,
          surname: payload?.surname,
          user_email: payload?.user_email,
          billing_method: payload?.billing_method,
          billing_email: payload?.billing_email,
          billing_address: payload?.billing_address,
          billing_zipcode: payload?.billing_zipcode,
          billing_city: payload?.billing_city,
          billing_country: payload?.billing_country,
          billing_monthly_price: payload?.billing_monthly_price,
          billing_global_reporter_price: payload?.billing_global_reporter_price,
          vat_number: payload?.vat_number,
          new_files: payload?.new_files,
          removed_files: payload?.removed_files,
        },
        options,
      );
    },
    [payload],
  );

  const handleUnlock = useCallback(() => {
    const id = toast.loading("Please wait...");
    Inertia.put(route("admin.agencies.unlock", organization.id), null, {
      onSuccess: () => showToast(id, "The account was successfully restored"),
      onError: () =>
        showToast(
          id,
          "Something went wrong when restoring the account",
          "error",
        ),
    });
  }, [organization.id]);

  useEffect(() => {
    formRef.current?.addEventListener("submit", submit);
    return () => {
      formRef.current?.removeEventListener("submit", submit);
    };
  }, [formRef, submit]);

  const isDisabled = useCallback(
    () => formRef.current?.checkValidity() !== true,
    [formRef],
  );

  return (
    <>
      <div className="mt-4 flex w-full items-center justify-end gap-4">
        <button
          className="text-[#F00000] underline hover:no-underline"
          onClick={() => setShowDeleteModal(true)}
          type="button"
        >
          Delete account
        </button>
        {organization.locked_at === null && (
          <button
            className="text-blue underline hover:no-underline"
            onClick={() => setShowInactivateModal(true)}
            type="button"
          >
            Inactivate account
          </button>
        )}
        {organization.locked_at && (
          <button
            className="text-blue underline hover:no-underline"
            onClick={handleUnlock}
            type="button"
          >
            Restore account
          </button>
        )}
        <InertiaLink
          className="text-blue underline hover:no-underline"
          href={route("admin.agencies")}
        >
          Cancel
        </InertiaLink>
        <button className="btn-blue grow-0" disabled={isDisabled()}>
          Save changes
        </button>
      </div>
      <AgencyDialogCenter
        organization={organization}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        showInactivateModal={showInactivateModal}
        setShowInactivateModal={setShowInactivateModal}
      />
    </>
  );
};
